


import React, { useEffect, useState } from 'react';
import { Box, Typography, Grid, Card, CardMedia, CardContent, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import videoFile from "../../src/assets/video.mp4";
import image1 from "../../src/assets/img 1.jpg";
import image2 from "../../src/assets/img 2.jpg";
import image3 from "../../src/assets/img 3.jpg";

const Home = () => {
    const [galleryImages, setGalleryImages] = useState([]);
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [galleryRes, videoRes] = await Promise.all([
                    axios.get('https://wedding-backend-pi.vercel.app/api/gallery/images'),
                    axios.get('https://wedding-backend-pi.vercel.app/api/videos/')
                ]);
                setGalleryImages(galleryRes.data.slice(0, 3)); // Show only 3 images
                setVideos(videoRes.data.slice(0, 3)); // Show only 3 videos
                setLoading(false);
            } catch (err) {
                console.error(err);
                setError('Failed to fetch data.');
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    // Static awards data
    const awards = [
        {
            title: "Best Wedding Photographer 2024",
            description: "Awarded for capturing the most memorable moments with creativity and elegance.",
            image: image1, // Replace with actual award image URL
        },
        {
            title: "Top Wedding Album 2024",
            description: "Recognized for creating the most beautiful wedding albums loved by couples.",
            image: image2, // Replace with actual award image URL
        },
        {
            title: "Customer's Choice Award 2024",
            description: "Honored for delivering exceptional service and customer satisfaction.",
            image: image3, // Replace with actual award image URL
        }
    ];

    if (loading) {
        return <Typography align="center" variant="h6">Loading...</Typography>;
    }

    if (error) {
        return <Typography align="center" variant="h6" color="error">{error}</Typography>;
    }

    return (
        <Box sx={{ width: '100%', overflowX: 'hidden' }}>
            {/* Hero Section */}
            <Box
                sx={{
                    position: 'relative',
                    height: { xs: '60vh', sm: '70vh', md: '100vh' },
                    width: '100%',
                    overflow: 'hidden',
                    textAlign: 'center',
                    color: '#fff',
                }}
            >
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        zIndex: -1,
                    }}
                >
                    <source src={videoFile} type="video/mp4" />
                </video>
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        textAlign: 'center',
                        zIndex: 1,
                        px: { xs: 2, sm: 4, md: 6 },
                    }}
                >
                    <Typography
                        variant="h2"
                        fontWeight="bold"
                        sx={{
                            fontSize: { xs: '2rem', sm: '3rem', md: '4rem' },
                            lineHeight: 1.2,
                        }}
                    >
                        Welcome to Wedding Bliss
                    </Typography>

                    <Typography
                        variant="h6"
                        sx={{
                            mt: 2,
                            fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' },
                            lineHeight: 1.4,
                        }}
                    >
                        Capturing your moments, one photo at a time.
                    </Typography>
                </Box>
            </Box>

            {/* Banner with Button */}
            <Box sx={{ py: 3, textAlign: 'center', backgroundColor: '#f3f3f3' }}>
                <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
                    Explore Our Stunning Portfolio
                </Typography>
                <Button
                    variant="contained"
                    size="large"
                    color="primary"
                    sx={{ fontWeight: 'bold' }}
                    onClick={() => navigate('/portfolio')}
                >
                    View Portfolio
                </Button>
            </Box>

            {/* Featured Weddings (Images - Only 3) */}
            <Box sx={{ py: 5, px: { xs: 2, md: 5 } }}>
                <Typography variant="h4" textAlign="center" fontWeight="bold" sx={{ mb: 4 }}>
                    Recent Weddings
                </Typography>
                <Grid container spacing={4} justifyContent="center">
                    {galleryImages.map((image, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    '&:hover .cardMedia': {
                                        transform: 'scale(1.1)',
                                        transition: 'transform 0.5s ease',
                                    },
                                }}
                                onClick={() => navigate('/blogs/gallery')} // Navigate to Gallery page
                            >
                                <CardMedia
                                    className="cardMedia"
                                    sx={{
                                        height: 0,
                                        paddingTop: '56.25%',
                                        transition: 'transform 0.5s ease',
                                    }}
                                    image={image.secure_url}
                                    title={image.title}
                                />
                                <CardContent
                                    sx={{
                                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                        color: 'white',
                                        position: 'absolute',
                                        bottom: 0,
                                        width: '100%',
                                        padding: 2,
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease',
                                        '&:hover': {
                                            opacity: 1,
                                        },
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                        {image.title}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>

            {/* Awards Section */}
            <Box sx={{ py: 5, px: { xs: 2, md: 5 }, backgroundColor: '#f9f9f9' }}>
                <Typography variant="h4" textAlign="center" fontWeight="bold" sx={{ mb: 4 }}>
                    Our Achievements & Awards
                </Typography>
                <Grid container spacing={4} alignItems="center">
                    {/* Left Side: Award Information */}
                    <Grid item xs={12} md={6}>
                        {awards.length > 0 ? (
                            awards.map((award, index) => (
                                <Box key={index} sx={{ mb: 3 }}>
                                    <Typography variant="h6" fontWeight="bold">
                                        {award.title}
                                    </Typography>
                                    <Typography variant="body1">{award.description}</Typography>
                                </Box>
                            ))
                        ) : (
                            <Typography variant="h6" color="textSecondary">
                                No awards available at the moment.
                            </Typography>
                        )}
                    </Grid>

                    {/* Right Side: Award Images */}
                    <Grid item xs={12} md={6}>
                        <Grid container spacing={2}>
                            {awards.map((award, index) => (
                                <Grid item xs={6} key={index}>
                                    <CardMedia
                                        component="img"
                                        image={award.image}
                                        alt={award.title}
                                        sx={{
                                            borderRadius: 2,
                                            boxShadow: 3,
                                            height: '150px',
                                            objectFit: 'cover',
                                        }}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>
            </Box>

            {/* Featured Videos (Only 3) */}
            <Box sx={{ py: 5, px: { xs: 2, md: 5 }, backgroundColor: '#f9f9f9' }}>
                <Typography variant="h4" textAlign="center" fontWeight="bold" sx={{ mb: 4 }}>
                    Featured Videos
                </Typography>
                <Grid container spacing={4} justifyContent="center">
                    {videos.map((video, index) => (
                        <Grid item xs={12} sm={6} md={4} key={index}>
                            <Card
                                sx={{
                                    position: 'relative',
                                    overflow: 'hidden',
                                    borderRadius: 2,
                                    cursor: 'pointer',
                                    '&:hover .cardMedia': {
                                        transform: 'scale(1.1)',
                                        transition: 'transform 0.5s ease',
                                    },
                                }}
                                onClick={() => navigate('/blogs/videos')} // Navigate to Videos page
                            >
                                <CardMedia
                                    className="cardMedia"
                                    sx={{
                                        height: 0,
                                        paddingTop: '56.25%',
                                        transition: 'transform 0.5s ease',
                                    }}
                                    image={video.thumbnail || image1} // Replace with video thumbnail
                                    title={video.title}
                                />
                                <CardContent
                                    sx={{
                                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                        color: 'white',
                                        position: 'absolute',
                                        bottom: 0,
                                        width: '100%',
                                        padding: 2,
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease',
                                        '&:hover': {
                                            opacity: 1,
                                        },
                                    }}
                                >
                                    <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold' }}>
                                        {video.title}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
};

export default Home;
